import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeftIcon, DownloadIcon, CheckCircleIcon, ClockIcon, XCircleIcon, AlertCircleIcon, FileTextIcon, PrinterIcon, ChevronRightIcon, ChevronLeftIcon, EyeIcon, PlusIcon, ChevronDownIcon, ArrowRightIcon } from 'lucide-react';
import { PDFPreviewModal } from '../../components/admin/settlements/PDFPreviewModal';
import { StatusChangeModal } from '../../components/admin/settlements/StatusChangeModal';
import { StatusFlowModal } from '../../components/admin/settlements/StatusFlowModal';
import { GenerateDocumentModal } from '../../components/admin/settlements/GenerateDocumentModal';
export const SettlementDetails = () => {
  const {
    id
  } = useParams();
  const [isLoading, setIsLoading] = useState(true);
  // Modal states
  const [pdfPreviewOpen, setPdfPreviewOpen] = useState(false);
  const [statusChangeOpen, setStatusChangeOpen] = useState(false);
  const [statusFlowOpen, setStatusFlowOpen] = useState(false);
  const [generateDocumentOpen, setGenerateDocumentOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [selectedDocumentType, setSelectedDocumentType] = useState<string | null>(null);
  // Mock data for a specific settlement
  const settlement = {
    id: 'S-2023-05-001',
    dealerName: 'TechHub Singapore',
    dealerId: 'D-001',
    amount: 12489.5,
    currency: 'SGD',
    ordersCount: 47,
    status: 'completed',
    paymentDate: '2023-05-15',
    createdDate: '2023-05-10',
    paymentMethod: 'Bank Transfer',
    transactionId: 'TRX-9876543210',
    dealerEmail: 'finance@techhub.sg',
    bankDetails: {
      bankName: 'DBS Bank',
      accountName: 'TechHub Singapore Pte Ltd',
      accountNumber: 'XXXX-XXXX-1234',
      swiftCode: 'DBSSSGSG'
    },
    settlementPeriod: {
      from: '2023-04-01',
      to: '2023-04-30'
    },
    commissionRate: 5,
    platformFee: 249.79,
    taxes: 874.27,
    netAmount: 12489.5,
    grossAmount: 13613.56,
    statusHistory: [{
      status: 'initiated',
      date: '2023-05-10',
      time: '08:05:30',
      user: 'System',
      description: 'Settlement created'
    }, {
      status: 'under_review',
      date: '2023-05-11',
      time: '11:20:05',
      user: 'System',
      description: 'Settlement under review'
    }, {
      status: 'approved',
      date: '2023-05-12',
      time: '16:45:10',
      user: 'Finance Manager',
      description: 'Settlement approved'
    }, {
      status: 'processing',
      date: '2023-05-14',
      time: '09:15:22',
      user: 'Admin User',
      description: 'Payment processing'
    }, {
      status: 'completed',
      date: '2023-05-15',
      time: '14:32:45',
      user: 'System',
      description: 'Payment completed'
    }]
  };
  // Document list
  const documents = [{
    id: 'invoice',
    name: 'Settlement Invoice',
    type: 'PDF',
    size: '245 KB',
    lastUpdated: '2023-05-10',
    status: 'generated',
    previewUrl: 'https://uploadthingy.s3.us-west-1.amazonaws.com/sTVL3NFLjSiFiLynqjbUJ9/image.png'
  }, {
    id: 'receipt',
    name: 'Payment Receipt',
    type: 'PDF',
    size: '128 KB',
    lastUpdated: '2023-05-15',
    status: 'generated',
    previewUrl: 'https://uploadthingy.s3.us-west-1.amazonaws.com/eQ3zS1QHFeDyfiCuiYuQyN/image.png'
  }, {
    id: 'tax_report',
    name: 'Tax Report',
    type: 'PDF',
    size: '315 KB',
    lastUpdated: '2023-05-10',
    status: 'generated',
    previewUrl: 'https://uploadthingy.s3.us-west-1.amazonaws.com/n97zhygTnEquGkke3MV2CD/image.png'
  }, {
    id: 'commission_report',
    name: 'Commission Report',
    type: 'PDF',
    size: '198 KB',
    lastUpdated: '2023-05-10',
    status: 'generated',
    previewUrl: 'https://uploadthingy.s3.us-west-1.amazonaws.com/sTVL3NFLjSiFiLynqjbUJ9/image.png'
  }];
  // Format date function
  const formatDate = dateString => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };
  // Simulate loading
  useEffect(() => {
    setIsLoading(true);
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 800);
    return () => clearTimeout(timer);
  }, []);
  // Status badge renderer
  const renderStatusBadge = status => {
    switch (status) {
      case 'completed':
        return <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800" data-id="element-1904">
            <CheckCircleIcon className="h-4 w-4 mr-2" data-id="element-1905" />
            Completed
          </span>;
      case 'pending':
        return <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-yellow-100 text-yellow-800" data-id="element-1906">
            <ClockIcon className="h-4 w-4 mr-2" data-id="element-1907" />
            Pending
          </span>;
      case 'processing':
        return <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800" data-id="element-1908">
            <ClockIcon className="h-4 w-4 mr-2" data-id="element-1909" />
            Processing
          </span>;
      case 'failed':
        return <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-800" data-id="element-1910">
            <XCircleIcon className="h-4 w-4 mr-2" data-id="element-1911" />
            Failed
          </span>;
      case 'cancelled':
        return <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-800" data-id="element-1912">
            <XCircleIcon className="h-4 w-4 mr-2" data-id="element-1913" />
            Cancelled
          </span>;
      default:
        return <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-800" data-id="element-1914">
            <AlertCircleIcon className="h-4 w-4 mr-2" data-id="element-1915" />
            Unknown
          </span>;
    }
  };
  const handleDocumentPreview = document => {
    setSelectedDocument(document);
    setPdfPreviewOpen(true);
  };
  const handleGenerateDocument = (documentType: string) => {
    setSelectedDocumentType(documentType);
    setGenerateDocumentOpen(true);
  };
  return <div className="max-w-5xl mx-auto space-y-8" data-id="element-1916">
      {/* Breadcrumb Navigation */}
      <nav className="flex items-center text-sm text-gray-500 mb-2" data-id="element-1917">
        <Link to="/admin/settlements" className="hover:text-gray-700" data-id="element-1918">
          Settlements
        </Link>
        <ChevronRightIcon className="h-4 w-4 mx-1" data-id="element-1919" />
        <span className="text-gray-700 font-medium" data-id="element-1920">{id}</span>
      </nav>
      {/* Main Content */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden" data-id="element-1921">
        {/* Header with navigation */}
        <div className="px-8 py-6 border-b border-gray-200 flex flex-col md:flex-row md:items-center md:justify-between gap-4" data-id="element-1922">
          <div className="flex items-start" data-id="element-1923">
            <Link to="/admin/settlements" className="mr-4 text-gray-500 hover:text-gray-700 hidden md:block" data-id="element-1924">
              <ArrowLeftIcon className="h-5 w-5" data-id="element-1925" />
            </Link>
            <div data-id="element-1926">
              <h1 className="text-2xl font-bold text-gray-900 flex items-center" data-id="element-1927">
                {isLoading ? <div className="h-8 w-48 bg-gray-200 rounded animate-pulse" data-id="element-1928"></div> : <>
                    Settlement {settlement.id}
                    <div className="ml-3" data-id="element-1929">
                      {renderStatusBadge(settlement.status)}
                    </div>
                    <button onClick={() => setStatusFlowOpen(true)} className="ml-3 text-sm font-medium text-blue-600 hover:text-blue-800" data-id="element-1930">
                      View Status Flow
                    </button>
                  </>}
              </h1>
              <div className="flex items-center text-sm text-gray-500 mt-1" data-id="element-1931">
                {isLoading ? <div className="h-4 w-32 bg-gray-200 rounded animate-pulse" data-id="element-1932"></div> : <>
                    <span data-id="element-1933">Created on {formatDate(settlement.createdDate)}</span>
                    <ChevronRightIcon className="h-4 w-4 mx-1" data-id="element-1934" />
                    <span data-id="element-1935">{settlement.dealerName}</span>
                  </>}
              </div>
            </div>
          </div>
          {!isLoading && <div className="flex items-center" data-id="element-1936">
              <button onClick={() => setStatusChangeOpen(true)} className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors flex items-center" data-id="element-1937">
                Change Status
                <ChevronDownIcon className="ml-2 h-4 w-4" data-id="element-1938" />
              </button>
            </div>}
        </div>
        {/* Key Metrics Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 border-b border-gray-200" data-id="element-1939">
          {isLoading ?
        // Skeleton loading for key metrics
        [...Array(4)].map((_, index) => <div key={`skeleton-metric-${index}`} className="p-6 border-r last:border-r-0 border-gray-200 animate-pulse" data-id="element-1940">
                <div className="h-4 w-24 bg-gray-200 rounded mb-2" data-id="element-1941"></div>
                <div className="h-6 w-32 bg-gray-200 rounded" data-id="element-1942"></div>
              </div>) : <>
              <div className="p-6 border-r border-gray-200" data-id="element-1943">
                <p className="text-sm font-medium text-gray-500" data-id="element-1944">Amount</p>
                <p className="text-xl font-bold text-gray-900 mt-1" data-id="element-1945">
                  {settlement.currency}{' '}
                  {settlement.amount.toLocaleString('en-US', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
              })}
                </p>
              </div>
              <div className="p-6 border-r border-gray-200" data-id="element-1946">
                <p className="text-sm font-medium text-gray-500" data-id="element-1947">Orders</p>
                <p className="text-xl font-bold text-gray-900 mt-1" data-id="element-1948">
                  {settlement.ordersCount}
                </p>
              </div>
              <div className="p-6 border-r border-gray-200" data-id="element-1949">
                <p className="text-sm font-medium text-gray-500" data-id="element-1950">Period</p>
                <p className="text-xl font-bold text-gray-900 mt-1" data-id="element-1951">
                  {formatDate(settlement.settlementPeriod.from)} -{' '}
                  {formatDate(settlement.settlementPeriod.to)}
                </p>
              </div>
              <div className="p-6" data-id="element-1952">
                <p className="text-sm font-medium text-gray-500" data-id="element-1953">
                  Payment Date
                </p>
                <p className="text-xl font-bold text-gray-900 mt-1" data-id="element-1954">
                  {settlement.paymentDate ? formatDate(settlement.paymentDate) : '-'}
                </p>
              </div>
            </>}
        </div>
        {/* Settlement Details */}
        {!isLoading && <div className="p-8" data-id="element-1955">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-1956">
              <div data-id="element-1957">
                <h3 className="text-lg font-medium text-gray-900 mb-4" data-id="element-1958">
                  Settlement Information
                </h3>
                <dl className="space-y-3" data-id="element-1959">
                  <div className="flex justify-between py-2 border-b border-gray-100" data-id="element-1960">
                    <dt className="text-sm text-gray-500" data-id="element-1961">Settlement ID</dt>
                    <dd className="text-sm font-medium text-gray-900" data-id="element-1962">
                      {settlement.id}
                    </dd>
                  </div>
                  <div className="flex justify-between py-2 border-b border-gray-100" data-id="element-1963">
                    <dt className="text-sm text-gray-500" data-id="element-1964">Created Date</dt>
                    <dd className="text-sm font-medium text-gray-900" data-id="element-1965">
                      {formatDate(settlement.createdDate)}
                    </dd>
                  </div>
                  <div className="flex justify-between py-2 border-b border-gray-100" data-id="element-1966">
                    <dt className="text-sm text-gray-500" data-id="element-1967">Payment Date</dt>
                    <dd className="text-sm font-medium text-gray-900" data-id="element-1968">
                      {settlement.paymentDate ? formatDate(settlement.paymentDate) : '-'}
                    </dd>
                  </div>
                  <div className="flex justify-between py-2 border-b border-gray-100" data-id="element-1969">
                    <dt className="text-sm text-gray-500" data-id="element-1970">Payment Method</dt>
                    <dd className="text-sm font-medium text-gray-900" data-id="element-1971">
                      {settlement.paymentMethod}
                    </dd>
                  </div>
                  <div className="flex justify-between py-2 border-b border-gray-100" data-id="element-1972">
                    <dt className="text-sm text-gray-500" data-id="element-1973">Transaction ID</dt>
                    <dd className="text-sm font-medium text-gray-900" data-id="element-1974">
                      {settlement.transactionId || '-'}
                    </dd>
                  </div>
                </dl>
              </div>
              <div data-id="element-1975">
                <h3 className="text-lg font-medium text-gray-900 mb-4" data-id="element-1976">
                  Dealer Information
                </h3>
                <div className="flex items-center mb-4" data-id="element-1977">
                  <div className="h-12 w-12 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-bold text-lg mr-4" data-id="element-1978">
                    {settlement.dealerName.charAt(0)}
                  </div>
                  <div data-id="element-1979">
                    <h3 className="font-medium text-gray-900" data-id="element-1980">
                      {settlement.dealerName}
                    </h3>
                    <p className="text-sm text-gray-500" data-id="element-1981">
                      ID: {settlement.dealerId}
                    </p>
                  </div>
                </div>
                <dl className="space-y-3" data-id="element-1982">
                  <div className="flex justify-between py-2 border-b border-gray-100" data-id="element-1983">
                    <dt className="text-sm text-gray-500" data-id="element-1984">Contact Person</dt>
                    <dd className="text-sm font-medium text-gray-900" data-id="element-1985">
                      John Lee
                    </dd>
                  </div>
                  <div className="flex justify-between py-2 border-b border-gray-100" data-id="element-1986">
                    <dt className="text-sm text-gray-500" data-id="element-1987">Email</dt>
                    <dd className="text-sm font-medium text-gray-900" data-id="element-1988">
                      {settlement.dealerEmail}
                    </dd>
                  </div>
                  <div className="flex justify-between py-2 border-b border-gray-100" data-id="element-1989">
                    <dt className="text-sm text-gray-500" data-id="element-1990">Bank Name</dt>
                    <dd className="text-sm font-medium text-gray-900" data-id="element-1991">
                      {settlement.bankDetails.bankName}
                    </dd>
                  </div>
                  <div className="flex justify-between py-2 border-b border-gray-100" data-id="element-1992">
                    <dt className="text-sm text-gray-500" data-id="element-1993">Account Number</dt>
                    <dd className="text-sm font-medium text-gray-900" data-id="element-1994">
                      {settlement.bankDetails.accountNumber}
                    </dd>
                  </div>
                </dl>
              </div>
            </div>
            <div className="mt-8" data-id="element-1995">
              <h3 className="text-lg font-medium text-gray-900 mb-4" data-id="element-1996">
                Financial Summary
              </h3>
              <div className="bg-gray-50 rounded-lg p-6" data-id="element-1997">
                <dl className="space-y-3" data-id="element-1998">
                  <div className="flex justify-between py-2 border-b border-gray-200" data-id="element-1999">
                    <dt className="text-sm text-gray-600" data-id="element-2000">Gross Amount</dt>
                    <dd className="text-sm font-medium text-gray-900" data-id="element-2001">
                      {settlement.currency}{' '}
                      {settlement.grossAmount.toLocaleString('en-US', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                  })}
                    </dd>
                  </div>
                  <div className="flex justify-between py-2 border-b border-gray-200" data-id="element-2002">
                    <dt className="text-sm text-gray-600" data-id="element-2003">
                      Commission ({settlement.commissionRate}%)
                    </dt>
                    <dd className="text-sm font-medium text-red-600" data-id="element-2004">
                      - {settlement.currency}{' '}
                      {(settlement.grossAmount * settlement.commissionRate / 100).toLocaleString('en-US', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                  })}
                    </dd>
                  </div>
                  <div className="flex justify-between py-2 border-b border-gray-200" data-id="element-2005">
                    <dt className="text-sm text-gray-600" data-id="element-2006">Platform Fee</dt>
                    <dd className="text-sm font-medium text-red-600" data-id="element-2007">
                      - {settlement.currency}{' '}
                      {settlement.platformFee.toLocaleString('en-US', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                  })}
                    </dd>
                  </div>
                  <div className="flex justify-between py-2 border-b border-gray-200" data-id="element-2008">
                    <dt className="text-sm text-gray-600" data-id="element-2009">Taxes</dt>
                    <dd className="text-sm font-medium text-red-600" data-id="element-2010">
                      - {settlement.currency}{' '}
                      {settlement.taxes.toLocaleString('en-US', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                  })}
                    </dd>
                  </div>
                  <div className="flex justify-between py-2 pt-3" data-id="element-2011">
                    <dt className="text-base font-medium text-gray-900" data-id="element-2012">
                      Net Amount
                    </dt>
                    <dd className="text-base font-bold text-gray-900" data-id="element-2013">
                      {settlement.currency}{' '}
                      {settlement.netAmount.toLocaleString('en-US', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                  })}
                    </dd>
                  </div>
                </dl>
              </div>
            </div>
          </div>}
      </div>
      {/* Document Section */}
      {!isLoading && <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden" data-id="element-2014">
          <div className="px-8 py-6 border-b border-gray-200 flex justify-between items-center" data-id="element-2015">
            <h2 className="text-xl font-medium text-gray-900" data-id="element-2016">
              Settlement Documents
            </h2>
            <div className="flex items-center space-x-3" data-id="element-2017">
              <button onClick={() => setGenerateDocumentOpen(true)} className="flex items-center px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors" data-id="element-2018">
                <PlusIcon className="h-4 w-4 mr-2" data-id="element-2019" />
                Generate Document
              </button>
              <button className="flex items-center px-4 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50 transition-colors" data-id="element-2020">
                <DownloadIcon className="h-4 w-4 mr-2" data-id="element-2021" />
                Download All
              </button>
            </div>
          </div>
          <div className="p-8" data-id="element-2022">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-2023">
              {documents.map(document => <div key={document.id} className="border border-gray-200 rounded-lg overflow-hidden hover:shadow-md transition-all duration-200" data-id="element-2024">
                  <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-b border-gray-200" data-id="element-2025">
                    <div className="flex items-center" data-id="element-2026">
                      <FileTextIcon className="h-5 w-5 text-blue-600 mr-2" data-id="element-2027" />
                      <span className="font-medium text-gray-900" data-id="element-2028">
                        {document.name}
                      </span>
                    </div>
                    <span className="text-xs px-2.5 py-1 bg-green-100 text-green-800 rounded-full" data-id="element-2029">
                      Generated
                    </span>
                  </div>
                  <div className="relative group" data-id="element-2030">
                    <div className="h-48 overflow-hidden bg-gray-100" data-id="element-2031">
                      <img src={document.previewUrl} alt={`${document.name} preview`} className="w-full h-full object-cover" data-id="element-2032" />
                    </div>
                    <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-200" data-id="element-2033">
                      <button onClick={() => handleDocumentPreview(document)} className="bg-white rounded-full p-3 shadow-md hover:bg-gray-100 transition-colors" data-id="element-2034">
                        <EyeIcon className="h-5 w-5 text-gray-700" data-id="element-2035" />
                      </button>
                    </div>
                  </div>
                  <div className="px-4 py-3 flex items-center justify-between" data-id="element-2036">
                    <div className="text-xs text-gray-500" data-id="element-2037">
                      <div className="flex items-center" data-id="element-2038">
                        <span className="font-medium text-blue-600 mr-1" data-id="element-2039">
                          PDF
                        </span>
                        <span data-id="element-2040">• {document.size}</span>
                      </div>
                      <div data-id="element-2041">Updated: {document.lastUpdated}</div>
                    </div>
                    <div className="flex items-center space-x-2" data-id="element-2042">
                      <button onClick={() => handleDocumentPreview(document)} className="p-1.5 text-gray-500 hover:text-blue-600 rounded-md hover:bg-blue-50" data-id="element-2043">
                        <EyeIcon className="h-4 w-4" data-id="element-2044" />
                      </button>
                      <button className="p-1.5 text-gray-500 hover:text-blue-600 rounded-md hover:bg-blue-50" data-id="element-2045">
                        <DownloadIcon className="h-4 w-4" data-id="element-2046" />
                      </button>
                      <button className="p-1.5 text-gray-500 hover:text-blue-600 rounded-md hover:bg-blue-50" data-id="element-2047">
                        <PrinterIcon className="h-4 w-4" data-id="element-2048" />
                      </button>
                    </div>
                  </div>
                </div>)}
            </div>
          </div>
        </div>}
      {/* Settlement Navigation */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 flex justify-between items-center" data-id="element-2049">
        <Link to="/admin/settlements/S-2023-04-002" className="flex items-center text-blue-600 hover:text-blue-800" data-id="element-2050">
          <ChevronLeftIcon className="h-5 w-5 mr-1" data-id="element-2051" />
          <span data-id="element-2052">Previous Settlement</span>
        </Link>
        <Link to="/admin/settlements/S-2023-05-002" className="flex items-center text-blue-600 hover:text-blue-800" data-id="element-2053">
          <span data-id="element-2054">Next Settlement</span>
          <ChevronRightIcon className="h-5 w-5 ml-1" data-id="element-2055" />
        </Link>
      </div>
      {/* Modals */}
      {pdfPreviewOpen && selectedDocument && <PDFPreviewModal document={selectedDocument} onClose={() => {
      setPdfPreviewOpen(false);
      setSelectedDocument(null);
    }} data-id="element-2056" />}
      {statusChangeOpen && <StatusChangeModal settlement={settlement} onClose={() => setStatusChangeOpen(false)} onStatusChange={newStatus => {
      // Handle status change logic
      setStatusChangeOpen(false);
    }} data-id="element-2057" />}
      {statusFlowOpen && <StatusFlowModal settlement={settlement} onClose={() => setStatusFlowOpen(false)} data-id="element-2058" />}
      {generateDocumentOpen && <GenerateDocumentModal settlement={settlement} documentType={selectedDocumentType} onClose={() => {
      setGenerateDocumentOpen(false);
      setSelectedDocumentType(null);
    }} onGenerate={documentType => {
      // Handle document generation logic
      setGenerateDocumentOpen(false);
      setSelectedDocumentType(null);
    }} data-id="element-2059" />}
    </div>;
};